import { Component, ViewChild, TemplateRef } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NgForm } from '@angular/forms';
import { Notification } from '../../models/notifications.model';
import { NotificationsService } from '../../services/notifications.service';
import { ClientSelectionService } from '../clients/client-selection.service';
import { finalize, map, distinctUntilChanged, takeUntil } from 'rxjs';
import { formatHttpErrorMessage } from '../../shared/utils/error.utils';
import { BaseTableComponent } from '../../shared/base/base-table.component';

@Component({
  selector: 'app-notifications',
  standalone: false,
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.css']
})
export class NotificationsComponent extends BaseTableComponent<Notification> {

  readonly displayedColumns = ['recipient', 'onETLSuccess', 'onETLFailure', 'onURFReceipt', 'actions'];
  @ViewChild('notificationDialog') dialogTemplate!: TemplateRef<any>;

  private dialogRef?: MatDialogRef<any>;
  model: Notification = this.emptyModel();
  clientId?: number;
  isEdit = false;
  originalRecipient = '';
  dialogError = '';
  override sortBy = 'recipient';
  override sortDir: 'asc' | 'desc' = 'asc';

  constructor(
    private dialog: MatDialog,
    private notificationsService: NotificationsService,
    private clientSelection: ClientSelectionService) {
      super();
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.resolveClientId();
  }

  openEdit(element: Notification): void {
    if (!this.clientId) return;
    this.isEdit = true;
    this.originalRecipient = element.recipient;
    this.model = { ...element, clientID: this.clientId };
    this.dialogError = '';
    this.errorMessage = '';
    this.dialogRef = this.dialog.open(this.dialogTemplate, { width: '460px', autoFocus: false, restoreFocus: false });
  }

  openNew(): void {
    if (!this.clientId) return;
    this.isEdit = false;
    this.model = this.emptyModel();
    this.dialogError = '';
    this.errorMessage = '';
    this.dialogRef = this.dialog.open(this.dialogTemplate, { width: '460px' });
  }

  closeDialog(): void {
    this.dialogRef?.close();
    this.dialogRef = undefined;
  }

  save(form?: NgForm): void {
    if (!this.clientId) return;

    if (form) {
      form.control.markAllAsTouched();
      if (form.invalid) return;
    }

    this.dialogError = '';
    const notification = this.buildNotificationPayload();
    const request = this.isEdit
      ? { clientID: this.clientId, notification: { ...notification, orgemail: this.originalRecipient } }
      : { clientID: this.clientId, notification };

    const observable = this.isEdit
      ? this.notificationsService.updateNotification(request)
      : this.notificationsService.addNotification(request);

    observable.subscribe({
      next: () => {
        console.log("from save");
        this.loadDataWrapper();
        this.closeDialog();
      },
      error: (err) => {
        this.dialogError = formatHttpErrorMessage('Failed to save notification', err);
      }
    });
  }

  protected override loadData(skip = 0, count = this.pageSize): void {
    if (!this.clientId) return;
    this.loading = true;
    this.errorMessage = '';

    this.notificationsService
      .getNotifications(this.clientId, skip, count, this.searchTerm, this.sortBy, this.sortDir)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (response: any) => {
          const list = response?.notifications ?? [];
          this.dataSource.data = list.map((n: any) => ({
            clientID: this.clientId!,
            recipient: n.Email,
            onETLSuccess: n.OnETLSuccess,
            onETLFailure: n.OnETLFailure,
            onURFReceipt: n.URFReceipt
          }));
          this.totalCount = response?.totalCount ?? this.dataSource.data.length;
        },
        error: (err) => {
          const sanitized = formatHttpErrorMessage(err.error, 'Failed to fetch notifications');
          const message = sanitized || `Failed to fetch notifications${err?.status ? ` (HTTP ${err.status})` : ''}.`;
          if (this.dialogRef) {
            this.dialogError = message;
          } else {
            this.errorMessage = message;
            this.dataSource.data = [];
            this.totalCount = 0;
          }
        }
      });
  }

  private resolveClientId(): void {
    this.clientSelection.selectedClient$
      .pipe(
        map(c => c?.ClientID ?? undefined),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(id => {
        this.clientId = id;
        this.pageIndex = 0;
        if (id != null) {
          this.errorMessage = '';
          this.loadDataWrapper();
        } else {
          this.dataSource.data = [];
          this.totalCount = 0;
          this.errorMessage = 'Please select a client to view notifications.';
        }
      });
  }

  private emptyModel(): Notification {
    return {
      clientID: this.clientId ?? 0,
      recipient: '',
      onETLSuccess: false,
      onETLFailure: false,
      onURFReceipt: false
    };
  }

  private buildNotificationPayload() {
    return {
      email: this.model.recipient,
      onETLSuccess: this.model.onETLSuccess,
      onETLFailure: this.model.onETLFailure,
      urfReceipt: this.model.onURFReceipt
    };
  }
}